import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

/*
|--------------------------------------------------------------------------
| Get Currencies
|--------------------------------------------------------------------------
*/
export const getCurrencies = createAsyncThunk(
    "currency/getCurrencies",
    async (data = {}, { rejectWithValue }) => {
        try {
            const {
                search = "",
                filter = "",
                perPage = 20,
                page = 1,
            } = data;

            const response = await api.get(
                `admin/currencies?search=${encodeURIComponent(search)}&filter=${encodeURIComponent(filter)}&perPage=${perPage}&page=${page}`
            );

            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                    "Failed to get currencies."
            );
        }
    }
);


/*
|--------------------------------------------------------------------------
| Create / Update Currency
|--------------------------------------------------------------------------
*/
export const saveCurrency = createAsyncThunk(
    "currency/saveCurrency",
    async (data, { rejectWithValue }) => {
        try {
            let response;

            if (data.id) {
                response = await api.post(
                    `admin/currencies/${data.id}`,
                    data
                );
            } else {
                response = await api.post(
                    "admin/currencies",
                    data
                );
            }

            return response;
        } catch (err) {
            console.log(err.response.data);
            return rejectWithValue(
                err.response?.data ||
                    "Failed to save currency."
            );
        }
    }
);


/*
|--------------------------------------------------------------------------
| Delete Currency
|--------------------------------------------------------------------------
*/
export const deleteCurrency = createAsyncThunk(
    "currency/deleteCurrency",
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.del(
                `admin/currencies/${id}`
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                    "Failed to delete currency."
            );
        }
    }
);


/*
|--------------------------------------------------------------------------
| Set Default Currency
|--------------------------------------------------------------------------
*/
export const setDefaultCurrency = createAsyncThunk(
    "currency/setDefaultCurrency",
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/currencies/${id}/default`
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                    "Failed to set default currency."
            );
        }
    }
);


/*
|--------------------------------------------------------------------------
| Sync Currency Exchange Rates
|--------------------------------------------------------------------------
*/
export const syncCurrencyRates = createAsyncThunk(
    "currency/syncCurrencyRates",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.post(
                "admin/currencies/sync"
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                    "Failed to sync currency rates."
            );
        }
    }
);


/*
|--------------------------------------------------------------------------
| Get Default Currency
|--------------------------------------------------------------------------
*/
export const getDefaultCurrency = createAsyncThunk(
    "currency/getDefaultCurrency",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(
                "admin/currencies/default"
            );

            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                    "Failed to get default currency."
            );
        }
    }
);


/*
|--------------------------------------------------------------------------
| Currency Slice
|--------------------------------------------------------------------------
*/
const currencySlice = createSlice({
    name: "currency",

    initialState: {
        list_currency: [],
        pagination: null,

        selected: null,
        default_currency: null,

        loading: false,
        list_loading: false,
        delete_loading: false,
        default_loading: false,
        sync_loading: false,

        status: "idle",

        error: null,
    },

    reducers: {

        /*
        |--------------------------------------------------------------------------
        | Selected Currency
        |--------------------------------------------------------------------------
        */

        setSelectedCurrency(state, action) {
            state.selected = action.payload;
        },

        unsetSelectedCurrency(state) {
            state.selected = null;
        },

        /*
        |--------------------------------------------------------------------------
        | Reset Status
        |--------------------------------------------------------------------------
        */

        resetStatus(state) {
            state.loading = false;
            state.list_loading = false;
            state.delete_loading = false;
            state.default_loading = false;
            state.sync_loading = false;
            state.status = "idle";
            state.error = null;
        },

        /*
        |--------------------------------------------------------------------------
        | Clear Error
        |--------------------------------------------------------------------------
        */

        clearCurrencyError(state) {
            state.error = null;
        },
    },

    extraReducers: (builder) => {

        /*
        |--------------------------------------------------------------------------
        | Get Currencies
        |--------------------------------------------------------------------------
        */

        builder

            .addCase(
                getCurrencies.pending,
                (state) => {
                    state.list_loading = true;
                    state.error = null;
                }
            )

            .addCase(
                getCurrencies.fulfilled,
                (state, action) => {

                    state.list_loading = false;

                    /*
                     * Laravel pagination response:
                     *
                     * {
                     *   status: true,
                     *   data: {
                     *      current_page: 1,
                     *      data: [...]
                     *   }
                     * }
                     */

                    state.list_currency =
                        action.payload?.data ||
                        [];

                    state.pagination =
                        action.payload?.data ||
                        null;

                    state.status = "succeeded";
                }
            )

            .addCase(
                getCurrencies.rejected,
                (state, action) => {
                    state.list_loading = false;
                    state.status = "failed";
                    state.error =
                        action.payload;
                }
            );


        /*
        |--------------------------------------------------------------------------
        | Save Currency
        |--------------------------------------------------------------------------
        */

        builder

            .addCase(
                saveCurrency.pending,
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )

            .addCase(
                saveCurrency.fulfilled,
                (state) => {
                    state.loading = false;
                    state.status = "succeeded";
                }
            )

            .addCase(
                saveCurrency.rejected,
                (state, action) => {
                    state.loading = false;
                    state.status = "failed";
                    state.error =
                        action.payload;
                }
            );


        /*
        |--------------------------------------------------------------------------
        | Delete Currency
        |--------------------------------------------------------------------------
        */

        builder

            .addCase(
                deleteCurrency.pending,
                (state) => {
                    state.delete_loading = true;
                    state.error = null;
                }
            )

            .addCase(
                deleteCurrency.fulfilled,
                (state) => {
                    state.delete_loading = false;
                    state.status = "succeeded";
                }
            )

            .addCase(
                deleteCurrency.rejected,
                (state, action) => {
                    state.delete_loading = false;
                    state.status = "failed";
                    state.error =
                        action.payload;
                }
            );


        /*
        |--------------------------------------------------------------------------
        | Set Default Currency
        |--------------------------------------------------------------------------
        */

        builder

            .addCase(
                setDefaultCurrency.pending,
                (state) => {
                    state.default_loading = true;
                    state.error = null;
                }
            )

            .addCase(
                setDefaultCurrency.fulfilled,
                (state, action) => {

                    state.default_loading = false;

                    /*
                     * Update local list so we don't
                     * need to wait for another API call.
                     */

                    state.list_currency =
                        state.list_currency.map(
                            (currency) => ({
                                ...currency,
                                is_default:
                                    currency.id ===
                                    action.meta.arg,
                            })
                        );

                    state.default_currency =
                        action.payload?.data ||
                        null;

                    state.status = "succeeded";
                }
            )

            .addCase(
                setDefaultCurrency.rejected,
                (state, action) => {
                    state.default_loading = false;
                    state.status = "failed";
                    state.error =
                        action.payload;
                }
            );


        /*
        |--------------------------------------------------------------------------
        | Sync Currency Rates
        |--------------------------------------------------------------------------
        */

        builder

            .addCase(
                syncCurrencyRates.pending,
                (state) => {
                    state.sync_loading = true;
                    state.error = null;
                }
            )

            .addCase(
                syncCurrencyRates.fulfilled,
                (state) => {
                    state.sync_loading = false;
                    state.status = "succeeded";
                }
            )

            .addCase(
                syncCurrencyRates.rejected,
                (state, action) => {
                    state.sync_loading = false;
                    state.status = "failed";
                    state.error =
                        action.payload;
                }
            );


        /*
        |--------------------------------------------------------------------------
        | Get Default Currency
        |--------------------------------------------------------------------------
        */

        builder

            .addCase(
                getDefaultCurrency.pending,
                (state) => {
                    state.default_loading = true;
                    state.error = null;
                }
            )

            .addCase(
                getDefaultCurrency.fulfilled,
                (state, action) => {

                    state.default_loading = false;

                    state.default_currency =
                        action.payload?.data ||
                        null;

                    state.status = "succeeded";
                }
            )

            .addCase(
                getDefaultCurrency.rejected,
                (state, action) => {
                    state.default_loading = false;
                    state.status = "failed";
                    state.error =
                        action.payload;
                }
            );
    },
});


export const {
    setSelectedCurrency,
    unsetSelectedCurrency,
    resetStatus,
    clearCurrencyError,
} = currencySlice.actions;


export default currencySlice.reducer;